import React, { useState, useMemo, useEffect } from 'react';
import {
  DndContext,
  closestCorners,
  DragOverlay,
  type DragEndEvent,
  type DragStartEvent,
} from '@dnd-kit/core';
import { Plus, Search, Filter } from 'lucide-react';
import { KANBAN_COLUMNS } from '../../services/whatsapp/waConstants';
import KanbanColumn from './KanbanColumn';
import KanbanCard, { type KanbanTicket } from './KanbanCard';
import CreateTicketModal from './CreateTicketModal';
import TicketDetailModal from './TicketDetailModal';
import { useKanbanStore } from '../../stores/whatsapp/kanbanStore';

const DONE_COLUMNS = ['done'];
const OPEN_COLUMNS = ['backlog', 'todo', 'in_progress', 'review'];

type StatusFilter = 'open' | 'closed' | 'all';

export default function KanbanPage() {
  const storeTickets = useKanbanStore(s => s.tickets);
  const loaded = useKanbanStore(s => s.loaded);
  const fetchTickets = useKanbanStore(s => s.fetchTickets);
  const storeAddTicket = useKanbanStore(s => s.addTicket);
  const moveTicket = useKanbanStore(s => s.moveTicket);
  const updateTicket = useKanbanStore(s => s.updateTicket);
  const deleteTicket = useKanbanStore(s => s.deleteTicket);

  useEffect(() => {
    if (!loaded) fetchTickets();
  }, [loaded, fetchTickets]);

  // Tickets vem direto do store (API)
  const tickets = storeTickets;
  const [activeTicket, setActiveTicket] = useState<KanbanTicket | null>(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedTicket, setSelectedTicket] = useState<KanbanTicket | null>(null);
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('open');
  const [filters, setFilters] = useState({ category: 'all', priority: 'all', search: '' });

  const filteredTickets = useMemo(() => {
    return tickets.filter(t => {
      // Filtro de status (aberto/fechado)
      if (statusFilter === 'open' && DONE_COLUMNS.includes(t.columnId)) return false;
      if (statusFilter === 'closed' && OPEN_COLUMNS.includes(t.columnId)) return false;
      if (filters.category !== 'all' && t.category !== filters.category) return false;
      if (filters.priority !== 'all' && t.priority !== filters.priority) return false;
      if (filters.search) {
        const q = filters.search.toLowerCase();
        if (!t.title.toLowerCase().includes(q) && !t.client.toLowerCase().includes(q)) return false;
      }
      return true;
    });
  }, [tickets, filters, statusFilter]);

  const visibleColumns = useMemo(() => {
    if (statusFilter === 'open') return KANBAN_COLUMNS.filter(c => OPEN_COLUMNS.includes(c.id));
    if (statusFilter === 'closed') return KANBAN_COLUMNS.filter(c => DONE_COLUMNS.includes(c.id));
    return KANBAN_COLUMNS;
  }, [statusFilter]);

  const handleDragStart = (event: DragStartEvent) => {
    const ticket = tickets.find(t => t.id === event.active.id);
    setActiveTicket(ticket || null);
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    setActiveTicket(null);
    if (!over) return;

    const activeId = active.id as string;
    const overId = over.id as string;
    const overColumn = KANBAN_COLUMNS.find(c => c.id === overId);
    const overTicket = tickets.find(t => t.id === overId);
    const targetColumnId = overColumn ? overColumn.id : overTicket?.columnId;
    if (!targetColumnId) return;

    moveTicket(activeId, targetColumnId);
  };

  const handleCreateTicket = (data: {
    title: string;
    description?: string;
    category: string;
    priority: string;
    assignee: string;
    client: string;
    dueDate: string;
    estimatedHours?: number;
  }) => {
    const initials = data.assignee
      .split(' ')
      .map(n => n[0])
      .join('');
    const newTicket: KanbanTicket = {
      id: `t${Date.now()}`,
      title: data.title,
      client: data.client,
      category: data.category,
      priority: data.priority,
      assignee: data.assignee,
      assigneeAvatar: initials,
      dueDate: data.dueDate || null,
      columnId: 'backlog',
      estimatedHours: data.estimatedHours,
      description: data.description,
    };
    storeAddTicket(newTicket);
  };

  const statusTabs: { key: StatusFilter; label: string; count: number }[] = [
    {
      key: 'open',
      label: 'Em aberto',
      count: tickets.filter(t => OPEN_COLUMNS.includes(t.columnId)).length,
    },
    {
      key: 'closed',
      label: 'Concluídas',
      count: tickets.filter(t => DONE_COLUMNS.includes(t.columnId)).length,
    },
    { key: 'all', label: 'Todas', count: tickets.length },
  ];

  return (
    <div className="min-h-full bg-[#f5f5f7]">
      {/* Header */}
      <div className="animate-fade-in mb-4 flex flex-col justify-between gap-3 sm:flex-row sm:items-center">
        <div>
          <h1 className="text-xl font-bold text-gray-900 sm:text-2xl">Quadro de Atividades</h1>
          <p className="mt-0.5 text-xs text-gray-500 sm:text-sm">
            Gerencie tickets e tarefas da equipe
          </p>
        </div>
        <button
          type="button"
          onClick={() => setShowCreateModal(true)}
          className="flex items-center justify-center gap-2 rounded-xl bg-green-600 px-4 py-2 text-sm font-semibold text-white shadow-sm transition-all hover:bg-green-700"
        >
          <Plus size={16} />
          <span className="hidden sm:inline">Novo Ticket</span>
          <span className="sm:hidden">Novo</span>
        </button>
      </div>

      {/* Status tabs */}
      <div className="mb-4 flex w-fit items-center gap-1 rounded-xl bg-white p-1 shadow-sm">
        {statusTabs.map(tab => (
          <button
            type="button"
            key={tab.key}
            onClick={() => setStatusFilter(tab.key)}
            className={`rounded-lg px-3 py-1.5 text-xs font-medium transition-all sm:text-sm ${
              statusFilter === tab.key
                ? 'bg-green-600 text-white shadow-sm'
                : 'text-gray-500 hover:bg-gray-50 hover:text-gray-700'
            }`}
          >
            {tab.label}
            <span
              className={`ml-1.5 rounded-full px-1.5 py-0.5 text-[10px] sm:text-xs ${
                statusFilter === tab.key ? 'bg-white/20' : 'bg-gray-100'
              }`}
            >
              {tab.count}
            </span>
          </button>
        ))}
      </div>

      {/* Filters */}
      <div className="animate-slide-up mb-4 flex flex-wrap items-center gap-2 sm:gap-3">
        <div className="relative min-w-[200px] max-w-[300px] flex-1">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            placeholder="Buscar tickets..."
            value={filters.search}
            onChange={e => setFilters(f => ({ ...f, search: e.target.value }))}
            className="w-full rounded-xl border border-gray-200 bg-white py-2 pl-9 pr-4 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-green-500/40"
          />
        </div>

        <div className="flex items-center gap-2">
          <div className="rounded-xl border border-gray-200 bg-white p-2 shadow-sm">
            <Filter size={16} className="text-gray-400" />
          </div>
          <select
            value={filters.category}
            onChange={e => setFilters(f => ({ ...f, category: e.target.value }))}
            className="rounded-xl border border-gray-200 bg-white px-3 py-2 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-green-500/40"
          >
            <option value="all">Categorias</option>
            <option value="fiscal">Fiscal</option>
            <option value="contabil">Contábil</option>
            <option value="pessoal">Depto. Pessoal</option>
            <option value="societario">Societário</option>
            <option value="consultoria">Consultoria</option>
          </select>

          <select
            value={filters.priority}
            onChange={e => setFilters(f => ({ ...f, priority: e.target.value }))}
            className="hidden rounded-xl border border-gray-200 bg-white px-3 py-2 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-green-500/40 sm:block"
          >
            <option value="all">Prioridades</option>
            <option value="low">Baixa</option>
            <option value="medium">Média</option>
            <option value="high">Alta</option>
            <option value="urgent">Urgente</option>
          </select>
        </div>

        <span className="ml-auto hidden text-xs font-medium text-gray-400 sm:block">
          {filteredTickets.length} de {tickets.length} tickets
        </span>
      </div>

      {/* Kanban Board - scroll horizontal com barra visível no topo */}
      <DndContext
        collisionDetection={closestCorners}
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
      >
        <div
          className="-mx-4 overflow-x-auto overscroll-x-contain px-4 pb-4 md:mx-0 md:px-0"
          style={{ scrollbarWidth: 'thin', scrollbarColor: '#d1d5db transparent' }}
        >
          {/* Trick: barra de scroll duplicada no topo */}
          <div className="flex gap-3" style={{ minWidth: `${visibleColumns.length * 290}px` }}>
            {visibleColumns.map(column => {
              const columnTickets = filteredTickets
                .filter(t => t.columnId === column.id)
                .map(t => ({ ...t, onClick: () => setSelectedTicket(t) }));
              return (
                <KanbanColumn
                  key={column.id}
                  column={column}
                  tickets={columnTickets}
                  onAddTicket={() => setShowCreateModal(true)}
                />
              );
            })}
          </div>
        </div>

        <DragOverlay dropAnimation={null}>
          {activeTicket ? <KanbanCard ticket={activeTicket} isOverlay /> : null}
        </DragOverlay>
      </DndContext>

      {/* Create Modal */}
      <CreateTicketModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onSubmit={handleCreateTicket}
      />

      {/* Detail Modal */}
      {selectedTicket && (
        <TicketDetailModal
          isOpen={!!selectedTicket}
          onClose={() => setSelectedTicket(null)}
          ticket={selectedTicket}
          onUpdate={updated => {
            updateTicket(updated.id, updated);
            setSelectedTicket(null);
          }}
          onDelete={id => {
            deleteTicket(id);
            setSelectedTicket(null);
          }}
          onMove={(id, newColumnId) => {
            moveTicket(id, newColumnId);
            setSelectedTicket(prev => (prev ? { ...prev, columnId: newColumnId } : null));
          }}
        />
      )}
    </div>
  );
}
